import {
  FloorHeatingTypes,
  FloorInsulationTypes,
  FloorScreedTypes,
} from "@iko-design-center/shared"
import { useHistory } from "react-router"
import { useRoutes } from "../routes/hooks"
import { Models } from "../../components/viewer/renderer.api"
import { useApplication } from "../application/hooks"
import { FloorData } from "../floor/store"
import { useFloor } from "../floor/hooks"
import { checkValidQueriedValueOfType, getEnumKey } from "./query-helpers"
import { useTranslation } from "react-i18next"

export function useQueryParams() {
  const { setCurrentModel } = useApplication()
  const { t } = useTranslation() as any
  const { Routes } = useRoutes()
  const history = useHistory()

  const {
    setFloorHeatingType,
    setFloorInsulationType,
    setFloorScreedType,
    setFloorInsulationTypeWithThickness,
  } = useFloor()

  const setQueryParams = (
    data: FloorData,
    language: string,
    redirect = false
  ) => {
    const languageRoute = t("routes.FLOOR_4_OVERVIEW", { lng: language })
    const urlWithoutParams = `${window.location.origin}${languageRoute}`
    const url = new URL(urlWithoutParams ?? window.location.href)

    // Floor heating
    const floorHeating = data.floorHeating.type
    const floorHeatingEnum = getEnumKey(floorHeating, FloorHeatingTypes)
    if (floorHeatingEnum) {
      url.searchParams.append("floor-heating", floorHeatingEnum)
    }

    // Floor insulation
    const floorInsulation = data.floorInsulation.type
    const floorInsulationEnum = getEnumKey(
      floorInsulation,
      FloorInsulationTypes
    )
    if (floorInsulationEnum) {
      url.searchParams.append("floor-insulation", floorInsulationEnum)

      // Include floor instulation thickness
      const floorInsulationThermalThickness =
        data.thermalPerformance.floorInsulation.thickness
      if (floorInsulationThermalThickness) {
        url.searchParams.append(
          "floor-insulation-thickness",
          floorInsulationThermalThickness.toString()
        )
      }
    }

    // Floor screed
    const floorScreed = data.floorScreed.type
    const floorScreedEnum = getEnumKey(floorScreed, FloorScreedTypes)
    if (floorScreedEnum) {
      url.searchParams.append("floor-screed", floorScreedEnum)
    }

    if (redirect) {
      window.location.href = url.href
    } else {
      window.history.replaceState([], document.title, url.href as string)
    }
  }

  const redirectBasedOnQueryParams = () => {
    const url = new URL(window.location.href)
    const query = {
      floorHeating: url.searchParams.get("floor-heating"),
      floorInsulation: url.searchParams.get("floor-insulation"),
      floorInsulationThickness: url.searchParams.get(
        "floor-insulation-thickness"
      ),
      floorScreed: url.searchParams.get("floor-screed"),
      detail: url.searchParams.get("detail"),
      token: url.searchParams.get("token"),
    }

    // Floor Heating
    const validFloorHeating = checkValidQueriedValueOfType(
      query.floorHeating,
      FloorHeatingTypes
    )
    if (validFloorHeating) setFloorHeatingType(validFloorHeating)

    // Floor Insulation
    const validFloorInsulation = checkValidQueriedValueOfType(
      query.floorInsulation,
      FloorInsulationTypes
    )
    if (validFloorInsulation) {
      // Include floor insulation thickness
      if (query.floorInsulationThickness) {
        const floorInsulationThickness = query.floorInsulationThickness
        if (floorInsulationThickness)
          setFloorInsulationTypeWithThickness(
            validFloorInsulation,
            floorInsulationThickness
          )
      } else {
        setFloorInsulationType(validFloorInsulation)
      }
    }

    // Floor Screed
    const validFloorScreed = checkValidQueriedValueOfType(
      query.floorScreed,
      FloorScreedTypes
    )
    if (validFloorScreed) setFloorScreedType(validFloorScreed)

    // Model
    const validModel = checkValidQueriedValueOfType(query.detail, Models)
    if (validModel) setCurrentModel(validModel)
    else setCurrentModel(Models.FLOOR)

    // Routing
    let route = Routes.FLOOR_1_FLOOR_HEATING
    if (validFloorHeating && validFloorInsulation && validFloorScreed) {
      route = Routes.FLOOR_4_OVERVIEW
    } else if (validFloorHeating && validFloorInsulation) {
      route = Routes.FLOOR_3_FLOOR_SCREED
    } else if (validFloorHeating) {
      route = Routes.FLOOR_2_FLOOR_INSULATION
    }

    if (query.token) {
      history.push({
        pathname: route as any,
        search: `?token=${query.token}`,
      })
    } else {
      history.push(route as any)
    }
  }

  return { setQueryParams, redirectBasedOnQueryParams }
}
